<template>
  <div>
    <!-- FILTER BAR -->
    <div ref="filterBar" class="spyder-filter-bar hidden sm:block bg-white bg-opacity-90 border-b border-r border-border-normal"
    style="backdrop-filter: blur(6px)">
      <FilterTopBar
        ref="filterTopBar"
        discovery
        :active-filters.sync="activeFilters"
        :selected-date-range.sync="selectedDateRange"
        :sort-order.sync="sortOrder"
        :text-search.sync="textSearch"
        @change="fetchAdvertisements"
      />
    </div>
    <!-- CONTENT -->
    <div class="px-4 pt-4 pb-4">
      <ContentAnalysis
        class="mb-4"
        :brand="brand"
        @tabSwitch="$emit('tabSwitch', $event)"
      />
      <AdvertisementList v-if="advertisements.length"
        discovery
        :advertisements="advertisements"
        :loading="loadingAdvertisements"
        :allAdsLoaded="allAdsLoaded"
        @updateAds="updateAdsLocal"
        @getMoreAds="() => getMoreAds({})"
      />
      <SpyderEmptyState
        v-else-if="!loadingAdvertisements"
        class="mt-16"
        :filters-applied="filtersApplied"
        :has-search-query="textSearch.length > 0"
        @clearFilters="clearFilters"
      />
      <BaseLoadingLogo v-if="loadingAdvertisements && !advertisements.length" />
      <infinite-loading
        v-if="advertisements.length"
        :identifier="infiniteId"
        @infinite="getMoreAds"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'
import { checkAndCacheAll, clearUniqueCreatives } from '@/db/UniqueCreativesDB'
import InfiniteLoading from 'vue-infinite-loading'
import EventBus from '@/plugins/BaseNotifications/EventBus'

import AdvertisementList from '../../advertisements/AdvertisementList.vue'
import ContentAnalysis from '../../library/ContentAnalysis.vue'
import SpyderEmptyState from '../SpyderEmptyState.vue'
import FilterTopBar from '../../search/FilterTopBar.vue'

export default {
  name: 'AdLibraryBrandView',
  components: {
    AdvertisementList,
    ContentAnalysis,
    SpyderEmptyState,
    FilterTopBar,
    InfiniteLoading
  },
  props: {
    brand: {
      type: Object,
      default: () => ({})
    },
    // Mirrors SpyderView's overage modal state; gates pagination while active.
    blocked: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // Data States
      advertisements: [],

      // Pagination States
      lastDocId: null,
      infiniteId: +new Date(),

      // Loading States
      loadingAdvertisements: true,
      allAdsLoaded: false,

      // Filter Bar States
      textSearch: '',
      activeFilters: {},
      selectedDateRange: {
        start: null,
        end: null
      },
      sortOrder: [{ name: 'Newest', value: 'desc' }]
    }
  },
  watch: {
    // Grouping toggled here or in Settings > Preferences
    getUniqueCreatives () {
      this.fetchAdvertisements()
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getUniqueCreatives']),
    filtersApplied () {
      return Object.keys(this.activeFilters).length > 0 || this.textSearch.length > 0
    }
  },
  mounted () {
    this.fetchAdvertisements()
    EventBus.$on('spyder:scroll', this.handleScroll)
  },
  beforeDestroy () {
    EventBus.$off('spyder:scroll', this.handleScroll)
  },
  methods: {
    async fetchAdvertisements (load = true, refresh = false) {
      this.loadingAdvertisements = load
      this.advertisements = []

      await clearUniqueCreatives()

      const ranBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange?.end) || null
      if (refresh) {
        this.lastDocId = null
      }
      try {
        const { data: results, nextPage } = await ForeplayAPI.Ads.getSpyderAds(
          this.$route.params.id, 
          null, 
          {
            ...this.activeFilters,
            ranBetweenStart,
            ranBetweenEnd,
            textSearch: this.textSearch,
            sort: this.sortOrder[0].value
          }
        )

        this.lastDocId = nextPage

        if (this.getUniqueCreatives) {
          const uniqueCreatives = await checkAndCacheAll(results)
          this.advertisements = uniqueCreatives
        } else {
          this.advertisements = results
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
      }
    },
    async getMoreAds ($state) {
      if (this.blocked) {
        return
      }

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.allAdsLoaded = true
        return
      }

      const ranBetweenStart = +new Date(this.selectedDateRange.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange.end) || null
      const { data: results, nextPage } = await ForeplayAPI.Ads.getSpyderAds(
        this.$route.params.id, 
        this.lastDocId, 
        {
          ...this.activeFilters,
          ranBetweenStart,
          ranBetweenEnd,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value
        }
      )
      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      if (this.getUniqueCreatives) {
        const uniqueCreatives = await checkAndCacheAll(results)
        this.advertisements = [...this.advertisements, ...uniqueCreatives]
      } else {
        this.advertisements = [...this.advertisements, ...results]
      }

      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    updateAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.advertisements]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.advertisements = duplicateAds
    },
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''

      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    handleScroll (position) {
      const bar = this.$refs.filterBar
      if (!bar) return

      const marginValue = position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding = position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      bar.style.marginLeft = `${marginValue}px`
      bar.style.marginRight = `${marginValue}px`
      bar.style.marginTop = `${marginValue}px`
      bar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue ? position.scrollTop / 15 : maxValue
      }

      if (position.scrollTop) {
        bar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          15
        )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          7
        )}px ${shadowSection(
          10
        )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          7
        )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          3
        )}px ${shadowSection(5)}px`
      } else {
        bar.style.boxShadow = null
      }
    }
  }
}
</script>

<style scoped>
.spyder-filter-bar {
  position: sticky;
  top: 16px;
  z-index: 40000;
}
</style>
